import React, { useContext, useEffect, useState } from 'react';
import { Components } from './styled';
import ProjectContainer from '../../components/containers/ProjectContainer';
import { Lesson, LESSON_TYPES } from '../../api/types/lesson';
import { getAllLessons } from '../../api/lesson';
import { ActivityIndicatorContext } from '../../providers/ActivityIndicatorProvider/context';
import ProjectTableHeader from '../../components/ProjectTableHeader';
import ProjectTable from '../../components/ProjectTable';
import { filteringModel, tableColumns } from '../../components/ProjectTable/constants';
import useFormTextState from '../../hooks/useFormTextState';
import useModalState from '../../hooks/useModalState';
import CreateOrEditLessonModal from '../../components/modals/CreateOrEditLessonModal';
import { AppSettingsContext } from '../../providers/AppSettingsProvider/context';
import useCustomizedDropdown from '../../hooks/useCustomizedDropdown';
import ProjectSearchDropdown from '../../components/ProjectSearchDropdown';
import ProjectPicker from '../../components/ProjectPicker';

const Lessons = () => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { categories, pillarTypes, pillarExpertises } = useContext(AppSettingsContext);

  const [data, setData] = useState<Lesson[]>([]);

  const searchProps = useFormTextState();
  const { props: pillarTypeProps } = useCustomizedDropdown({
    possibleValues: pillarTypes,
    singleSelect: true,
    customType: 'pillarType',
  });
  const { props: pillarExpertiseProps } = useCustomizedDropdown({
    possibleValues: pillarExpertises,
    singleSelect: true,
    customType: 'pillarExpertise',
  });
  const { props: categoryProps } = useCustomizedDropdown({
    possibleValues: categories,
    singleSelect: true,
  });
  const { props: lessonTypeProps } = useCustomizedDropdown({
    possibleValues: LESSON_TYPES,
    singleSelect: true,
    customType: 'lessonType',
  });

  const createLessonModalProps = useModalState<{ lesson?: Lesson; publishedAtDate?: Date }>();

  const refreshData = async () => {
    setIsLoading(true);
    const result = await getAllLessons({
      category: categoryProps.selectedItems?.[0]?.value,
      pillarType: pillarTypeProps.selectedItems?.[0]?.value,
      pillarExpertise: pillarExpertiseProps.selectedItems?.[0]?.value,
      lessonType: lessonTypeProps.selectedItems?.[0]?.value,
    });
    setIsLoading(false);
    if (result && Array.isArray(result)) {
      setData(result);
    }
  };

  useEffect(() => {
    refreshData().then();
  }, [
    pillarTypeProps.selectedItems,
    pillarExpertiseProps.selectedItems,
    categoryProps.selectedItems,
    lessonTypeProps.selectedItems,
  ]);

  const onEditLesson = (id: string) => {
    const foundLesson = data?.find((item) => item?.id === id);
    if (foundLesson) {
      createLessonModalProps.open({ lesson: foundLesson });
    }
  };

  const onAddLesson = () => {
    createLessonModalProps.open({ lesson: undefined });
  };

  const onCreateOrEditModalClose = (refreshTrigger?: boolean) => {
    if (refreshTrigger) {
      refreshData?.().then();
    }
    createLessonModalProps.onModalClose();
  };

  const _filteringModel = filteringModel([
    { columnField: 'searchValue', value: searchProps.value, operatorValue: 'contains' },
  ]);
  const _sortingModel = { field: 'publishedAt', sort: 'desc' };

  return (
    <ProjectContainer title={'Lessons'}>
      <Components.Container>
        <ProjectTableHeader
          onSearchChange={searchProps.onChangeText}
          searchLabel={'Search by lesson title or ID'}
          searchPlaceholder={'Enter search term'}>
          <Components.SearchBarChildrenWrapper>
            <Components.SearchBarSeparator />
            <Components.FiltersWrapper>
              <ProjectPicker dropdownProps={pillarTypeProps} notSetText={'Pillar type'} />
              <ProjectSearchDropdown {...pillarTypeProps} />
              <ProjectPicker dropdownProps={pillarExpertiseProps} notSetText={'Pillar expertise'} />
              <ProjectSearchDropdown {...pillarExpertiseProps} />
              <ProjectPicker dropdownProps={categoryProps} notSetText={'Category'} />
              <ProjectSearchDropdown {...categoryProps} />
              <ProjectPicker dropdownProps={lessonTypeProps} notSetText={'Lesson type'} />
              <ProjectSearchDropdown {...lessonTypeProps} />
            </Components.FiltersWrapper>
            <Components.SearchBarSeparator />
            <Components.PrimaryButton onClick={onAddLesson}>Add Lesson</Components.PrimaryButton>
          </Components.SearchBarChildrenWrapper>
        </ProjectTableHeader>

        <Components.TableWrapper>
          <ProjectTable
            data={data || []}
            columns={tableColumns.lessons({ onEdit: onEditLesson })}
            filteringModel={_filteringModel}
            sortingModel={_sortingModel}
            showRowCountText={'Found lessons'}
          />
        </Components.TableWrapper>
      </Components.Container>
      <CreateOrEditLessonModal {...createLessonModalProps} onModalClose={onCreateOrEditModalClose} />
    </ProjectContainer>
  );
};

export default Lessons;
